import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { DollarSign, EyeIcon, FileText, Pencil, Receipt, Users } from 'lucide-react'
import { useEffect, useState } from 'react'

import { partnersQuotes } from 'rails_routes'
import { ButtonLink } from 'slab'
import { useWindowLocation } from 'utils/windowLocation'

import { CreateQuoteButton } from './CreateQuoteButton'
import { CreateQuoteFailed } from './GetErrorBanners'
import { getTypeForCensusStatus } from './ProspectCensusDetails'
import { hasQuoteGenerationStarted } from './QuoteDetails'
import { QuoteDetailsSheet } from './QuoteDetailsSheet'
import { type Quote } from './types'
import { getLocaleDateString, getTimezoneDatetimeString } from '../../utils'
import { type GrapeAPIErrorType } from '../hooks/GrapeAPIError'
import { useGetQuotes } from '../hooks/useGetQuotes'
import Badge from '../reusable/Badge'
import TanstackTable, {
  type PaginationMetadata,
} from '../reusable/table/TanstackTable'

const columnHelper = createColumnHelper<Quote>()
const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: (info) => {
      const value = info.getValue()
      if (!value) {
        const quoteDate = info.row.original.created_at
          ? new Date(info.row.original.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
          : null
        return `${info.row.original.quoting_prospect_name} quote ${quoteDate}`
      }

      return value.length > 30 ? `${value.substring(0, 30)}...` : value
    },
  }),
  columnHelper.accessor(
    (row) => ({
      content: (
        <div>
          <div>
            {getTimezoneDatetimeString(new Date(row.created_at), 'EST')}
          </div>
          <div className='caption text-gray-500'>
            {row.census_file_name}
          </div>
        </div>
      ),
    }),
    {
      header: 'Date created (EST) & census',
      id: 'date_created_and_census',
      cell: (info) => info.getValue().content,
    }
  ),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => (
      <Badge
        type={
          info.getValue() ? getTypeForCensusStatus(info.getValue()!) : 'default'
        }
        text={info.getValue() ?? 'Unknown'}
      />
    ),
  }),
  columnHelper.accessor('coverage_start_date', {
    header: 'Coverage start date',
    cell: (info) =>
      info.getValue()
        ? getLocaleDateString(new Date(info.getValue()!))
        : undefined,
  }),
]

const PAGE_SIZE = 10

function CreateNewQuoteCover ({
  isForAdminView,
  prospectId,
  onCreateQuoteError,
  quotingV2NewQuotePath,
}: {
  isForAdminView: boolean;
  prospectId: string;
  onCreateQuoteError: (error: GrapeAPIErrorType) => void;
  quotingV2NewQuotePath?: string;
}) {
  const handleCreateQuoteError = (error: GrapeAPIErrorType) => {
    onCreateQuoteError(error)
  }

  return (
    <>
      <p>No quotes have been created for this prospect yet.</p>
      {isForAdminView && (
        <>
          <p className='mb-4'>
            To get started, click "Create new quote" below.
          </p>
          {quotingV2NewQuotePath
            ? (
              <ButtonLink
                href={quotingV2NewQuotePath}
                variant='secondary'
                leftIcon={Receipt}
              >
                Create new quote
              </ButtonLink>
              )
            : <CreateQuoteButton
                prospectId={prospectId}
                onShowError={handleCreateQuoteError}
                secondary
              />}
        </>
      )}
      {!isForAdminView && (
        <div className='mt-4'>
          <ButtonLink
            href={partnersQuotes.new({ prospect_id: prospectId })}
            variant='secondary'
            leftIcon={Receipt}
          >
            Create new quote
          </ButtonLink>
        </div>
      )}
    </>
  )
}

export function QuotesList ({
  isForAdminView,
  prospectId,
  quotingV2NewQuotePath,
  quotingV2DynamicQuoteEditorPath,
  quotingV2FlatQuoteEditorPath,
}: {
  isForAdminView: boolean;
  prospectId: string;
  quotingV2NewQuotePath?: string;
  quotingV2DynamicQuoteEditorPath?: string;
  quotingV2FlatQuoteEditorPath?: string;
}) {
  const { assign } = useWindowLocation()

  const [showDetail, setShowDetail] = useState<string | undefined>()
  const [createQuoteError, setCreateQuoteError] =
    useState<GrapeAPIErrorType | null>(null)

  const handleCreateQuoteError = (errorObj: GrapeAPIErrorType) => {
    setCreateQuoteError(errorObj)
  }

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: PAGE_SIZE,
  })

  const { data, isLoading, isError } = useGetQuotes({
    prospectId,
    pagination,
  })

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
    },
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data?.pagination)
    }
  }, [data?.pagination])

  return (
    <>
      {showDetail && (
        <QuoteDetailsSheet
          prospectId={prospectId}
          quoteId={showDetail}
          onClose={() => setShowDetail(undefined)}
          quotingV2DynamicQuoteEditorPath={quotingV2DynamicQuoteEditorPath}
          quotingV2FlatQuoteEditorPath={quotingV2FlatQuoteEditorPath}
        />
      )}
      {createQuoteError && (
        <CreateQuoteFailed className='mb-4' errorObj={createQuoteError} />
      )}
      <TanstackTable<Quote>
        table={table}
        isLoading={isLoading}
        isError={isError}
        noDataMessage={
          <CreateNewQuoteCover
            isForAdminView={isForAdminView}
            prospectId={prospectId}
            onCreateQuoteError={handleCreateQuoteError}
            quotingV2NewQuotePath={quotingV2NewQuotePath}
          />
        }
        onRowClick={({ row }) => {
          if (hasQuoteGenerationStarted(row.original) || row.original.has_flat_allowances) {
            setShowDetail(row.original.id)
          } else {
            const recordId = row.original.id
            assign(`${prospectId}/quote/${recordId}`)
          }
        }}
        paginationLabelSingular='quote'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.next_page || 0,
          })}
        onPreviousPageClick={() =>
          setPagination({
            ...pagination,
            pageIndex: paginationMetadata?.prev_page || 0,
          })}
      />
    </>
  )
}

function QuoteCard ({
  isForAdminView,
  quote,
  onClick,
}: {
  isForAdminView: boolean;
  quote: Quote;
  onClick: () => void;
}) {
  const {
    created_at: createdAt,
    eligible_employee_count: eligibleEmployeeCount,
    census_file_name: censusFileName,
  } = quote

  const getQuoteDisplayName = () => {
    if (quote.name) {
      return quote.name
    }

    const quoteDate = createdAt
      ? new Date(createdAt).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' })
      : null
    return `${quote.quoting_prospect_name} quote ${quoteDate}`
  }

  return (
    <div
      className='flex items-center justify-between p-4 border border-ds-divider rounded-md mb-3 bg-white shadow-sm cursor-pointer hover:bg-ds-background-hover'
      onClick={onClick}
      role='button'
      tabIndex={0}
    >
      <div className='flex flex-col'>
        <div className='font-medium text-ds-text-primary'>
          {getQuoteDisplayName()}
        </div>
        <div className='flex items-center flex-wrap gap-x-3 caption text-gray-500 mt-1'>
          {quote.total_monthly_employer_contribution != null && (
            <span className='flex items-center gap-x-1 whitespace-nowrap'>
              <DollarSign size={12} />
              {quote.total_monthly_employer_contribution.formatted_amount.replace(/^\$/, '')} / mo.
            </span>
          )}
          {eligibleEmployeeCount != null && (
            <span className='flex items-center gap-x-1 whitespace-nowrap'>
              <Users size={12} />
              {eligibleEmployeeCount} eligible employees
            </span>
          )}
          {censusFileName && (
            <span className='flex items-center gap-x-1 whitespace-nowrap'>
              <FileText size={12} />
              {censusFileName}
            </span>
          )}
        </div>
      </div>

      <div className='flex items-center justify-end gap-x-2 flex-shrink-0 ml-4'>
        {quote.status === 'unprocessed' && !quote.has_flat_allowances && (
          <ButtonLink
            href={`/${isForAdminView ? 'admin' : 'broker'}/prospects/${quote.quoting_prospect_id}/quotes/${quote.id}/edit`}
            variant='secondary'
            size='small'
            leftIcon={Pencil}
          >
            Complete Quote
          </ButtonLink>
        )}
        {quote.status !== 'unprocessed' && (
          <div className='flex gap-x-2'>
            <ButtonLink
              href={`/${isForAdminView ? 'admin' : 'broker'}/prospects/${quote.quoting_prospect_id}/quotes/${quote.id}/edit`}
              variant='secondary'
              size='small'
              leftIcon={Pencil}
            >
              Edit
            </ButtonLink>
            <ButtonLink
              href={`/${isForAdminView ? 'admin' : 'broker'}/prospects/${quote.quoting_prospect_id}/quotes/${quote.id}/${quote.has_flat_allowances ? 'flat_allowances' : 'explore'}`}
              variant='secondary'
              size='small'
              leftIcon={EyeIcon}
            >
              View
            </ButtonLink>
          </div>
        )}
        {/* TODO: Pull the share modal out into a separate page that we can link to directly */}
        {/* <ButtonLink href={partnersQuotes.explore({ prospect_id: quote.quoting_prospect_id, quote_id: quote.id })} variant='secondary' size='small' leftIcon={Share}>
          Share
        </ButtonLink> */}
      </div>

    </div>
  )
}

export function QuotesListV2 ({
  isForAdminView,
  prospectId,
  quotingV2NewQuotePath,
}: {
  isForAdminView: boolean;
  prospectId: string;
  quotingV2NewQuotePath?: string;
  quotingV2QuoteEditorPath?: string;
}) {
  const { assign } = useWindowLocation()

  const [createQuoteError, setCreateQuoteError] =
    useState<GrapeAPIErrorType | null>(null)

  const handleCreateQuoteError = (errorObj: GrapeAPIErrorType) => {
    setCreateQuoteError(errorObj)
  }

  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: PAGE_SIZE,
  })

  const { data, isLoading, isError } = useGetQuotes({
    prospectId,
    pagination,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data?.pagination)
    }
  }, [data?.pagination])

  const handleQuoteClick = (quote: Quote) => {
    const recordId = quote.id
    if (quote.status === 'unprocessed') {
      assign(`/${isForAdminView ? 'admin' : 'broker'}/prospects/${prospectId}/quotes/${recordId}/edit`)
    } else if (quote.has_flat_allowances) {
      assign(`/${isForAdminView ? 'admin' : 'broker'}/prospects/${prospectId}/quotes/${recordId}/flat_allowances`)
    } else {
      assign(`/${isForAdminView ? 'admin' : 'broker'}/prospects/${prospectId}/quotes/${recordId}/explore`)
    }
  }

  return (
    <>
      {createQuoteError && (
        <CreateQuoteFailed className='mb-4' errorObj={createQuoteError} />
      )}

      {isLoading && <p>Loading quotes...</p>}

      {isError && !isLoading && <p>Error loading quotes.</p>}

      {!isLoading && !isError && data && (
        <>
          {data.data.length === 0 && (
            <CreateNewQuoteCover
              isForAdminView={isForAdminView}
              prospectId={prospectId}
              onCreateQuoteError={handleCreateQuoteError}
              quotingV2NewQuotePath={quotingV2NewQuotePath}
            />
          )}
          {data.data.map((quote) => (
            <QuoteCard
              isForAdminView={isForAdminView}
              key={quote.id}
              quote={quote}
              onClick={() => handleQuoteClick(quote)}
            />
          ))}

          {paginationMetadata && paginationMetadata.total_pages > 1 && (
            <div className='flex justify-between items-center mt-4'>
              <ButtonLink
                onClick={() =>
                  setPagination({
                    ...pagination,
                    pageIndex: paginationMetadata?.prev_page || 1,
                  })}
                disabled={!paginationMetadata?.prev_page}
                variant='tertiary'
                size='small'
              >
                Previous
              </ButtonLink>
              <span>
                Page {paginationMetadata.current_page} of {paginationMetadata.total_pages}
              </span>
              <ButtonLink
                onClick={() =>
                  setPagination({
                    ...pagination,
                    pageIndex: paginationMetadata?.next_page || paginationMetadata.total_pages,
                  })}
                disabled={!paginationMetadata?.next_page}
                variant='tertiary'
                size='small'
              >
                Next
              </ButtonLink>
            </div>
          )}
        </>
      )}
    </>
  )
}
